/**
 * A2-6: minimal in-app notification store. There is no `@mantine/notifications`
 * dependency in this project (and A2-6 forbids adding runtime deps), so we keep
 * a tiny framework-agnostic pub/sub here and render it with `@mantine/core`'s
 * `Notification` in {@link ../components/NotificationHost}. Keeping the store
 * free of React/DOM makes the global error wiring unit-testable in node.
 */
export type NoticeLevel = 'error' | 'success' | 'info';

export interface Notice {
  readonly id: number;
  readonly level: NoticeLevel;
  readonly message: string;
  readonly title?: string;
}

type Listener = (notices: readonly Notice[]) => void;

let notices: readonly Notice[] = [];
let seq = 0;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener(notices);
}

/** Subscribe to the notice list. Fires immediately with the current list. */
export function subscribeNotices(listener: Listener): () => void {
  listeners.add(listener);
  listener(notices);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissNotice(id: number): void {
  const next = notices.filter((n) => n.id !== id);
  if (next.length !== notices.length) {
    notices = next;
    emit();
  }
}

function push(level: NoticeLevel, message: string, title?: string): number {
  const id = ++seq;
  notices = [...notices, { id, level, message, title }];
  emit();
  return id;
}

/** Fire-and-forget notifications used by the global error handler and pages. */
export const notify = {
  error: (message: string, title = 'Fehler'): number => push('error', message, title),
  success: (message: string, title?: string): number => push('success', message, title),
  info: (message: string, title?: string): number => push('info', message, title),
};

/** Test helpers (not for production use). */
export function _getNotices(): readonly Notice[] {
  return notices;
}
export function _resetNotices(): void {
  notices = [];
  seq = 0;
  listeners.clear();
}
